import { createReducer, on } from "@ngrx/store";
import { AddRow, ModifyRow, SetLibrary, UndoAction, RedoAction, LibrarySaved, MarkRowToDelete, DeleteRows, MoveRowToPosition, AddRowFromClipboard, SortRowsAlphabetically } from "./library.actions";
import { ModifiedLibrary } from "../../models/libraryModel/modifiedLibrary.model";
import { ExtendedRow } from "../../models/libraryModel/extendedRow.model";
import { tabGroupMapping } from "src/app/services/tabGroupMapping";

// Define the initial state of the library
export const initialState: Readonly<ModifiedLibrary & 
  { 
    allowedTabNames: string[], 
    allowedRows: (ExtendedRow & {order: number})[], 
    nextId: number, 
    initialRows: any[], 
    selectedRowsIdsToDelete: Set<number>,
    modifications: {added: ExtendedRow[], modified: ExtendedRow[], deleted: ExtendedRow[]},
    past: any[], 
    future: any[],
    // orderBy: {columnName: string, order: "asc" | "desc"}
  }
> = { 
  tableName: "", 
  columns: [], 
  rows: [], 
  tabNames: [],
  allowedTabNames: [],
  allowedRows: [],
  nextId: 1,
  initialRows: [],
  selectedRowsIdsToDelete: new Set<number>(),
  modifications: {added: [], modified: [], deleted: []},
  past: [],
  future: [],
  // orderBy: {columnName: "", order: "asc"},
};

function getMostRepeatedSysReferenceData(rows: ExtendedRow[], tabName: string): string | undefined {
  const sysReferenceDataCounts: { [key: string]: number } = rows
  .filter(row => row.tabName === tabName)
  .reduce((counts: { [key: string]: number }, row) => {
    const sysReferenceData = row['sysreferencedata'] || '';
    counts[sysReferenceData] = (counts[sysReferenceData] || 0) + 1;
    return counts;
  }, {});
  const sysReferenceDataCountsArray = Object.entries(sysReferenceDataCounts);
  console.log("sysReferenceDataCountsArray", sysReferenceDataCountsArray);
  
  const maxRepetitions = Math.max(...sysReferenceDataCountsArray.map(([_, count]) => count));
  return sysReferenceDataCountsArray.filter(([_, count]) => count === maxRepetitions).map(([sysReferenceData, _]) => sysReferenceData)[0];
}

// Create the library reducer to handle actions and update the state
export const LibraryReducer = createReducer(
  initialState,
  
  // Handle the SetLibrary action to set the library data in the state
  on(SetLibrary, (state, {library, userGroups, showAllTabs}) => { 
      // Get the allowed tab group mapping for the library
      let allowedTabGroupMapping: { [key: string]: string[] } = tabGroupMapping[library.tableName];

      // Initialize new allowed tab names
      const newAllowedTabNames = library.tabNames.filter(name => 
        name !== "" 
        && (
          allowedTabGroupMapping === undefined
          || showAllTabs
          || allowedTabGroupMapping[name]?.some(group => userGroups.includes(group))
        )
      );

      const newRows = library.rows;
      
      // Filter the allowed rows based on the allowed tab names or if the tab name is empty
      const newAllowedRows = newRows.map((row, index) => ({...row, order: index})).filter(row => row.tabName === "" || newAllowedTabNames.includes(row.tabName));
      
    return {
      tableName: library.tableName,
      columns: library.columns,
      rows: newRows,
      tabNames: library.tabNames,
      allowedTabNames: newAllowedTabNames,
      allowedRows: newAllowedRows,
      nextId: library.rows.length + 1,
      initialRows: library.rows,
      selectedRowsIdsToDelete: new Set<number>(),
      modifications: {added: [], modified: [], deleted: []},
      past: [],
      future: [],
      // orderBy: {...state.orderBy, columnName: library.columns[0].columnName},
    };
  }),
  
  // Handle the AddRow action to add a new row to the state
  on(AddRow, (state, { tabName }) => {
    const mostRepeatedSysReferenceData = getMostRepeatedSysReferenceData(state.rows, tabName);
    const newRow: ExtendedRow = { id: state.nextId, tabName, sysreferencedata: mostRepeatedSysReferenceData == undefined ? '' : mostRepeatedSysReferenceData };
    console.log(newRow);
    
    var newModifications = {added: [...state.modifications.added], modified: [...state.modifications.modified], deleted: [...state.modifications.deleted]};

    if (!state.initialRows.find(row => row.id === newRow.id)) {
      newModifications.added.push(newRow);
    }

    const tabNameStartIndex = state.rows.findIndex(row => row.tabName === tabName);
    const updatedRows = [
      ...state.rows.slice(0, tabNameStartIndex),
      newRow,
      ...state.rows.slice(tabNameStartIndex)
    ];

    const newState = {
      ...state,
      rows: updatedRows,
      allowedRows: updatedRows.map((row, index) => ({...row, order: index})).filter(row => row.tabName === "" || state.allowedTabNames.includes(row.tabName)),
      nextId: state.nextId + 1,
      modifications: newModifications
    };

    return {
      ...newState,
      past: [...state.past, state],
      future: []
    };
  }),
  
  on(AddRowFromClipboard, (state, { tabName, rows }) => {
    const mostRepeatedSysReferenceData = getMostRepeatedSysReferenceData(state.rows, tabName);
    var newModifications = {added: [...state.modifications.added], modified: [...state.modifications.modified], deleted: [...state.modifications.deleted]};
    const newRows: ExtendedRow[] = [];
    const invalidRows = rows.filter(rowData => rowData.length+1 !== state.columns.length);  // Check if the number of columns in the row matches the expected number of columns except for filter (sysreferencedata) column
    
    if (invalidRows.length > 0) {      
      alert(`${invalidRows.length} invalid rows were found with unexpected columns, the expected ${state.columns.filter(column => column.columnName !== 'sysreferencedata').length} columns are ${state.columns.filter(column => column.columnName !== 'sysreferencedata').map(column => column.columnName).join(", ").replace("syshelp", "help")}`);
      return state;
    }

    rows.forEach((rowData, index) => {
      let newRow: ExtendedRow = { id: state.nextId + index, tabName, sysreferencedata: mostRepeatedSysReferenceData == undefined ? '' : mostRepeatedSysReferenceData };      
      
      rowData.forEach((value, columnIndex) => {
        const column = state.columns[columnIndex];
        if (column && newRow[column.columnName] !== '') {
          newRow[column.columnName] = column.type === "double" ? Number(value) : value;
        }
      });

      newRows.push(newRow);

      if (!state.initialRows.find(row => row.id === newRow.id)) {
        newModifications.added.push(newRow);
      }
    })

    const tabNameStartIndex = state.rows.findIndex(row => row.tabName === tabName);

    const updatedRows = [
      ...state.rows.slice(0, tabNameStartIndex),
      ...newRows,
      ...state.rows.slice(tabNameStartIndex)
    ];

    const newState = {
      ...state,
      rows: updatedRows,
      allowedRows: updatedRows.map((row, index) => ({...row, order: index})).filter(row => row.tabName === "" || state.allowedTabNames.includes(row.tabName)),
      nextId: state.nextId + rows.length,
      modifications: newModifications
    };

    return {
      ...newState,
      past: [...state.past, state],
      future: []
    };
  }),

  // Handle the ModifyRow action to modify an existing row in the state
  on(ModifyRow, (state, { key, value, id }) => {
    const rowIndex = state.rows.findIndex(row => row.id === id);
    if (rowIndex === -1) {
      return state;
    }

    const dataType = state.columns.find(column => column.columnName === key)?.type;
  
    console.log(value);
    

    const updatedRow : ExtendedRow = { 
      id,
      tabName: state.rows[rowIndex].tabName,
      ...Object.fromEntries(Object.entries(state.rows[rowIndex]).filter(([k, v]) => k !== key)), 
      ...(value !== "" ? { [key]: dataType === "double" ? Number(value) : value } : {}) 
    };
    console.log(updatedRow);
    
    
    const updatedRows = [
      ...state.rows.slice(0, rowIndex),
      updatedRow,
      ...state.rows.slice(rowIndex + 1)
    ];

    var newModifications = {added: [...state.modifications.added], modified: [...state.modifications.modified], deleted: [...state.modifications.deleted]};

    if (!state.modifications.added.find(row => row.id === id)) {      
      const index = state.modifications.modified.findIndex(row => row.id === id);
      if (
        state.initialRows.find(row => row.id === id) && 
        String(state.initialRows.find(row => row.id === id)[key]) !== String(value)
      ) {
        if (index !== -1) {
          newModifications.modified[index] = updatedRow;
        } else {
          newModifications.modified.push(updatedRow);
        }
      } else {
        if (index !== -1) {
          newModifications.modified.splice(index, 1);
        }
      }
    } else {
      const index = newModifications.added.findIndex(row => row.id === id);
      if (index !== -1) {
        newModifications.added[index] = updatedRow;
      }
    }
  
    const newState = {
      ...state,
      rows: updatedRows,      
      allowedRows: updatedRows.map((row, index) => ({...row, order: index})).filter(row => row.tabName === "" || state.allowedTabNames.includes(row.tabName)),
      modifications: newModifications
    };

    let newPast = [...state.past, state]

    if (state.past.length >= 10)
      newPast = newPast.slice(-10,-1)

    return {
      ...newState,
      past: newPast,
      future: []
    };
  }),

  on(MarkRowToDelete, (state, { id, status }) => {
    const newSelectedRowsToDelete = new Set(state.selectedRowsIdsToDelete);

    if (status) {
      newSelectedRowsToDelete.add(id);
    } else {
      newSelectedRowsToDelete.delete(id);
    }    
    
    let newPast = [...state.past, state]

    if (state.past.length >= 10)
      newPast = newPast.slice(-10,-1)

    return {
      ...state,
      past: newPast,
      future: [],
      selectedRowsIdsToDelete: newSelectedRowsToDelete
    };
  }),
  
  // Handle the DeleteRow action to delete a row from the state
  on(DeleteRows, (state) => {
    const updatedRows = state.rows.filter(row => !state.selectedRowsIdsToDelete.has(row.id));
    const updatedAllowedRows = state.rows.map((row, index) => ({...row, order: index})).filter(row => !state.selectedRowsIdsToDelete.has(row.id));
    
    const newState = {
      ...state,
      rows: updatedRows,
      allowedRows: updatedAllowedRows
    };
    
    var newModifications = {added: [...state.modifications.added], modified: [...state.modifications.modified], deleted: [...state.modifications.deleted]};

    state.selectedRowsIdsToDelete.forEach(id => {
      // Check if the row is in the initial rows and update modifications accordingly
      if (state.initialRows.find(row => row.id === id)) {
        const deletedRow = state.initialRows.find(row => row.id === id);

        //remove from the modified rows
        const index = newModifications.modified.findIndex(row => row.id === id);
        if (index !== -1) {
          newModifications.modified.splice(index, 1);
        }

        newModifications.deleted.push(deletedRow);
      } else {
        // If the row is not in the initial rows, remove it from added rows
        const index = newModifications.added.findIndex(row => row.id === id);
        if (index !== -1) {
          newModifications.added.splice(index, 1);
        }
      }
    });
    
    return {
      ...newState,
      selectedRowsIdsToDelete: new Set<number>(),
      modifications: newModifications
    };
  }),

  on(MoveRowToPosition, (state, { id, newPosition }) => {
    const rowIndex = state.rows.findIndex(row => row.id === id);
    if (rowIndex === -1) {
      return state;
    }

    const updatedRows = [...state.rows];
    const movedRow = updatedRows.splice(rowIndex, 1)[0];
    updatedRows.splice(newPosition, 0, movedRow);

    const newState = {
      ...state,
      rows: updatedRows,
      allowedRows: updatedRows.map((row, index) => ({...row, order: index})).filter(row => row.tabName === "" || state.allowedTabNames.includes(row.tabName))
    };

    let newPast = [...state.past, state];

    if (state.past.length >= 10) {
      newPast = newPast.slice(-10, -1);
    }

    return {
      ...newState,
      past: newPast,
      future: []
    };
  }),

  on(SortRowsAlphabetically, (state, {tabName, columnName, direction }) => {
    const column = state.columns.find(c => c.columnName === columnName);
    const isNumeric = column?.type === 'double' || column?.type === 'int';

    const compareValues = (valueA: any, valueB: any): number => {
      const aEmpty = valueA === undefined || valueA === null || valueA === "";
      const bEmpty = valueB === undefined || valueB === null || valueB === "";

      // Always push blank values to the front, regardless of sort direction,
      // so the comparator stays consistent/transitive for Array.sort().
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return -1;
      if (bEmpty) return 1;

      const result = isNumeric
        ? Number(valueA) - Number(valueB)
        : String(valueA).localeCompare(String(valueB));

      return direction === 'asc' ? result : -result;
    };

    const sortedRows = [...state.rows].filter(row => row.tabName === tabName).sort((a, b) => compareValues(a[columnName], b[columnName]));

    const tabNameStartIndex = state.rows.findIndex(row => row.tabName === tabName);
    const tabNameEndIndex = state.rows.findIndex(row => row.tabName !== tabName, tabNameStartIndex);

    const updatedRows = [
      ...state.rows.slice(0, tabNameStartIndex).filter(row => row.tabName !== tabName),
      ...sortedRows,
      ...state.rows.slice(tabNameEndIndex).filter(row => row.tabName !== tabName)
    ];

    const newState = {
      ...state,
      rows: updatedRows,
      allowedRows: updatedRows.map((row, index) => ({...row, order: index})).filter(row => row.tabName === "" || state.allowedTabNames.includes(row.tabName))
    };

    let newPast = [...state.past, state];

    if (state.past.length >= 10) {
      newPast = newPast.slice(-10, -1);
    }

    return {
      ...newState,
      past: newPast,
      future: []
    };
  }),

  // Handle Undo action to revert to previous state
  on(UndoAction, (state) => {
    if (state.past.length === 0) return state;
    
    const previousState = state.past[state.past.length - 1];
    const newPast = state.past.slice(0, -1);

    return {
      ...previousState,
      past: newPast,
      future: [state, ...state.future]
    };
  }),

  // Handle Redo action to revert to next state
  on(RedoAction, (state) => {
    if (state.future.length === 0) return state;

    const nextState = state.future[0];
    const newFuture = state.future.slice(1);

    return {
      ...nextState,
      past: [...state.past, state],
      future: newFuture
    };
  }),

  // Handle LibrarySaved action to reset the state after saving
  on(LibrarySaved, (state) => {
    return {
      ...state,
      selectedRowsIdsToDelete: new Set<number>(),
      modifications: {added: [], modified: [], deleted: []},
      past: [],
      future: []
    };
  })

);